import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { DisruptionFormValues } from '@/pages/Disruptions/types'
import { useGetDisruptionReference } from './useGetDisruptionReference'
import { useDisruptionsTableFormStateStore } from '../store'
import { useRef } from 'react'

interface ICreateDisruptionRequest
    extends Omit<DisruptionFormValues, 'routes' | 'diversionRoutes' | 'delayInMinutes' | 'nmccInformed'> {
    routes: string[]
    diversionRoutes: { diversion: string | null; route: string }[]
    reference: string
    nmccInformed: boolean | null
    delayInMinutes: number | null
}

export const useCreateDisruption = () => {
    const queryClient = useQueryClient()
    const { data: reference } = useGetDisruptionReference()
    const { clearInputValues, setDraftCreatedReference, setNewlyCreatedReference, setPublishedDisruptionBanner } =
        useDisruptionsTableFormStateStore()
    const { selectedRoutes } = useDisruptionsTableFormStateStore()

    const isDraftRef = useRef(false)
    const capturedReferenceRef = useRef<string | undefined>(undefined)
    const capturedStartRef = useRef<string | undefined>(undefined)

    const createDisruptionMutation = useAppMutation<void, Error, ICreateDisruptionRequest>({
        mutationFn: (data) =>
            createInstance({
                url: '/disruptions',
                method: 'POST',
                data,
            }),
        alertOnError: true,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['GET_DISRUPTIONS'] })
            await queryClient.invalidateQueries({ queryKey: ['disruption-reference'] })
            clearInputValues()
            if (capturedReferenceRef.current) {
                setNewlyCreatedReference(capturedReferenceRef.current)
            }
            if (isDraftRef.current && capturedReferenceRef.current) {
                setDraftCreatedReference(capturedReferenceRef.current)
            } else if (capturedReferenceRef.current && capturedStartRef.current) {
                setPublishedDisruptionBanner({
                    reference: capturedReferenceRef.current,
                    start: capturedStartRef.current,
                })
            }
        },
    })

    const handleCreateDisruption = (data: DisruptionFormValues) => {
        if (!reference) throw new Error('Reference not found')

        isDraftRef.current = !data.location || !data.details || !data.start
        capturedReferenceRef.current = reference
        capturedStartRef.current = data.start

        const nonEmptyDiversionRoutes = data.diversionRoutes
            .filter((route) => route.route)
            .map((route) => ({
                diversion: route.diversion === '' ? null : route.diversion,
                route: route.route,
            }))

        const emptyDiversionRoutes = selectedRoutes
            .filter((route) => !nonEmptyDiversionRoutes.map((route) => route.route).includes(route))
            .map((route) => ({
                diversion: null,
                route,
            }))

        const allDiversionRoutes = [...nonEmptyDiversionRoutes, ...emptyDiversionRoutes]

        createDisruptionMutation.mutate({
            status: data.status,
            reference,
            routes: selectedRoutes,
            diversionRoutes:
                selectedRoutes.length === 1
                    ? [
                          {
                              diversion:
                                  data.diversionRoutes[0].diversion === '' ? null : data.diversionRoutes[0].diversion,
                              route: selectedRoutes[0],
                          },
                      ]
                    : allDiversionRoutes,
            location: data.location,
            details: data.details,
            disruptionType: data.disruptionType,
            delayInMinutes: data.delayInMinutes ? Number(data.delayInMinutes) : null,
            nmccInformed: data.nmccInformed === 'Yes' ? true : data.nmccInformed === 'No' ? false : null,
            start: data.start,
            end: data.end,
        })
    }

    return { handleCreateDisruption, isPending: createDisruptionMutation.isPending }
}
